import PlainDate from '@Domain/PlainDate';
import type FieldDraft from '@UI/Pages/Subjects/Shared/FormData/FieldDraft';
import type SubjectFieldDefinitionView from '@UI/Store/SubjectTypes/Views/SubjectFieldDefinitionView';
import * as React from 'react';
import { Calendar, Checkbox, Dropdown, InputNumber, InputText, MultiSelect, TextArea } from '@verclam/ui-kit';

type Props = {
	readonly definition: SubjectFieldDefinitionView;
	readonly value: FieldDraft;
	readonly onChange: (value: FieldDraft) => void;
	readonly errorMessage: string | undefined;
	readonly isFormFieldValid: boolean;
};

type Handlers = {
	readonly onText: (value: string) => void;
	readonly onNumber: (value: number | null) => void;
	readonly onDate: (value: Date | null) => void;
	readonly onBoolean: (value: boolean) => void;
	readonly onChoice: (value: string | null) => void;
	readonly onChoices: (value: readonly string[]) => void;
};

type InputContext = Props & { readonly handlers: Handlers };

type CommonFieldProps = {
	readonly name: string;
	readonly label: string;
	readonly required: boolean;
	readonly errorMessage: string | undefined;
	readonly isFormFieldValid: boolean;
};

const renderText = ({ value, handlers }: InputContext, common: CommonFieldProps): React.ReactNode => (
	<InputText {...common} value={typeof value === 'string' ? value : ''} onChange={handlers.onText} />
);

const renderLongText = ({ value, handlers }: InputContext, common: CommonFieldProps): React.ReactNode => (
	<TextArea
		{...common}
		autoResize
		rows={4}
		value={typeof value === 'string' ? value : ''}
		onChange={handlers.onText}
	/>
);

const renderDate = ({ value, handlers }: InputContext, common: CommonFieldProps): React.ReactNode => {
	const date = typeof value === 'string' ? PlainDate.fromApi(value) : null;

	return (
		<Calendar
			{...common}
			dateFormat="dd/mm/yy"
			value={date === null ? null : date.toCalendar()}
			onChange={handlers.onDate}
		/>
	);
};

const renderNumber = ({ value, handlers }: InputContext, common: CommonFieldProps): React.ReactNode => (
	<InputNumber {...common} value={typeof value === 'number' ? value : null} onChange={handlers.onNumber} />
);

const renderBoolean = ({ value, handlers }: InputContext, common: CommonFieldProps): React.ReactNode => (
	<Checkbox {...common} checked={value === true} onChange={handlers.onBoolean} />
);

const renderChoice = (
	{ definition, value, handlers }: InputContext,
	common: CommonFieldProps,
): React.ReactNode => (
	<Dropdown
		{...common}
		options={definition.options}
		optionLabel="label"
		optionValue="value"
		showClear={!definition.required}
		value={typeof value === 'string' ? value : null}
		onChange={handlers.onChoice}
	/>
);

const renderMultipleChoice = (
	{ definition, value, handlers }: InputContext,
	common: CommonFieldProps,
): React.ReactNode => (
	<MultiSelect
		{...common}
		options={definition.options}
		optionLabel="label"
		optionValue="value"
		display="chip"
		value={Array.isArray(value) ? [...value] : []}
		onChange={handlers.onChoices}
	/>
);

const RENDERERS: Readonly<
	Record<string, (context: InputContext, common: CommonFieldProps) => React.ReactNode>
> = Object.freeze({
	text: renderText,
	long_text: renderLongText,
	date: renderDate,
	number: renderNumber,
	boolean: renderBoolean,
	choice: renderChoice,
	multiple_choice: renderMultipleChoice,
});

/** The control matching the nature of a sheet field; an unknown nature is announced, not rendered. */
const FieldValueInput: React.FC<Props> = (props) => {
	const { definition, onChange, errorMessage, isFormFieldValid } = props;

	const handlers = React.useMemo<Handlers>(
		() => ({
			onText: (value) => onChange(value),
			onNumber: (value) => onChange(value),
			onDate: (value) => onChange(value === null ? '' : PlainDate.fromCalendar(value).toApi()),
			onBoolean: (value) => onChange(value),
			onChoice: (value) => onChange(value),
			onChoices: (value) => onChange(value),
		}),
		[onChange],
	);

	const render = RENDERERS[definition.nature];

	if (render === undefined) {
		return <p>Champ non pris en charge</p>;
	}

	const common: CommonFieldProps = {
		name: definition.key,
		label: definition.label,
		required: definition.required && definition.nature !== 'boolean',
		errorMessage,
		isFormFieldValid,
	};

	return <React.Fragment>{render({ ...props, handlers }, common)}</React.Fragment>;
};

export default FieldValueInput;
